import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, Wrench, BarChart3, Bell, TrendingUp, Activity, Search, Ban, Trash2, Crown, UserCog, Eye, Globe2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface Stats {
  totalUsers: number;
  totalToolUsage: number;
  todayUsage: number;
  weekUsage: number;
}

interface UserProfile {
  id: string;
  username: string | null;
  avatar_url: string | null;
  is_admin: boolean | null;
  is_premium: boolean | null;
  premium_tier: string | null;
  created_at: string | null;
}

const USERS_PER_PAGE = 50;

const Admin = () => {
  const { isAdmin, loading } = useProfile();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({ totalUsers: 0, totalToolUsage: 0, todayUsage: 0, weekUsage: 0 });
  const [recentUsers, setRecentUsers] = useState<UserProfile[]>([]);
  const [toolStats, setToolStats] = useState<{ tool_name: string; count: number }[]>([]);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, loading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchRecentUsers();
      fetchToolStats();
      fetchAllUsers();
    }
  }, [isAdmin]);

  const fetchStats = async () => {
    const today = new Date().toISOString().split('T')[0];
    const weekAgo = subDays(new Date(), 7).toISOString();

    const [usersRes, totalUsageRes, todayUsageRes, weekUsageRes] = await Promise.all([
      supabase.from('profiles').select('id', { count: 'exact', head: true }),
      supabase.from('tool_usage').select('id', { count: 'exact', head: true }),
      supabase.from('tool_usage').select('id', { count: 'exact', head: true }).gte('used_at', today),
      supabase.from('tool_usage').select('id', { count: 'exact', head: true }).gte('used_at', weekAgo),
    ]);

    setStats({
      totalUsers: usersRes.count || 0,
      totalToolUsage: totalUsageRes.count || 0,
      todayUsage: todayUsageRes.count || 0,
      weekUsage: weekUsageRes.count || 0,
    });
  };

  const fetchRecentUsers = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, username, avatar_url, is_premium, is_admin, premium_tier, created_at')
      .order('created_at', { ascending: false })
      .limit(5);
    setRecentUsers(data || []);
  };

  const fetchAllUsers = async (page = currentPage) => {
    const from = page * USERS_PER_PAGE;
    const to = from + USERS_PER_PAGE - 1;
    const { data, count } = await supabase
      .from('profiles')
      .select('id, username, avatar_url, is_premium, is_admin, premium_tier, created_at', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);
    setAllUsers(data || []);
    setTotalUsers(count || 0);
  };

  const fetchToolStats = async () => {
    const { data } = await supabase
      .from('tool_usage')
      .select('tool_name');

    if (data) {
      const counts: Record<string, number> = {};
      data.forEach((row) => {
        counts[row.tool_name] = (counts[row.tool_name] || 0) + 1;
      });

      const statsArray = Object.entries(counts)
        .map(([tool_name, count]) => ({ tool_name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      setToolStats(statsArray);
    }
  };

  const callAdminAction = async (action: string, targetUserId: string, value: any) => {
    const { data, error } = await supabase.functions.invoke('admin-actions', {
      body: { action, targetUserId, value },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const toggleAdmin = async (userId: string, currentStatus: boolean | null) => {
    try {
      await callAdminAction('toggle_admin', userId, !currentStatus);
      toast.success(currentStatus ? 'Admin access revoked' : 'Admin access granted');
      fetchAllUsers();
      fetchRecentUsers();
    } catch (error) {
      console.error('Error updating admin status:', error);
      toast.error('Failed to update admin status');
    }
  };

  const togglePremium = async (userId: string, currentStatus: boolean | null) => {
    try {
      await callAdminAction('toggle_premium', userId, !currentStatus);
      toast.success(currentStatus ? 'Premium removed' : 'Premium granted');
      fetchAllUsers();
      fetchRecentUsers();
    } catch (error) {
      console.error('Error updating premium status:', error);
      toast.error('Failed to update premium status');
    }
  };

  const deleteUser = async (userId: string) => {
    try {
      await callAdminAction('delete_user', userId, null);
      toast.success('User deleted successfully');
      setIsUserDialogOpen(false);
      fetchAllUsers();
      fetchRecentUsers();
      fetchStats();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  // Sort: premium first, then admin, then regular
  const sortedUsers = [...allUsers].sort((a, b) => {
    if (a.is_premium && !b.is_premium) return -1;
    if (!a.is_premium && b.is_premium) return 1;
    if (a.is_admin && !b.is_admin) return -1;
    if (!a.is_admin && b.is_admin) return 1;
    return 0;
  });

  const filteredUsers = sortedUsers.filter(user =>
    user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const premiumCount = allUsers.filter(u => u.is_premium).length;

  const getInitials = (username: string | null) => {
    if (username) return username.slice(0, 2).toUpperCase();
    return 'U';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  if (!isAdmin) {
    return null;
  }

  const statCards = [
    { label: 'Total Users', value: stats.totalUsers, icon: Users, color: 'text-primary', bg: 'bg-primary/10' },
    { label: 'Premium Users', value: premiumCount, icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
    { label: 'Total Tool Usage', value: stats.totalToolUsage, icon: Wrench, color: 'text-success', bg: 'bg-success/10' },
    { label: 'Today Usage', value: stats.todayUsage, icon: Activity, color: 'text-warning', bg: 'bg-warning/10' },
    { label: 'Week Usage', value: stats.weekUsage, icon: TrendingUp, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header with Gradient */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/20 via-primary/10 to-accent/20 p-6 border border-primary/20">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative flex items-center gap-4">
          <div className="p-4 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 shadow-lg shadow-primary/20 animate-pulse">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">Manage users, tools, and analytics</p>
          </div>
          <div className="ml-auto hidden sm:flex items-center gap-2">
            <Badge className="bg-primary/20 text-primary border-primary/30">
              <Activity className="h-3 w-3 mr-1" />
              Live
            </Badge>
          </div>
        </div>
      </div>

      {/* Stats Grid with Animations */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card
            key={stat.label}
            className="glass border-border/50 transition-all duration-300 hover:border-primary/50 hover:-translate-y-2 hover:shadow-xl hover:shadow-primary/10 group overflow-hidden relative"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <CardContent className="pt-6 relative">
              <div className="flex items-center gap-4">
                <div className={cn(
                  "p-3 rounded-xl transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg",
                  stat.bg,
                  "group-hover:shadow-" + stat.color.replace('text-', '') + "/30"
                )}>
                  <stat.icon className={cn("h-6 w-6 transition-all duration-300 group-hover:rotate-3", stat.color)} />
                </div>
                <div>
                  <p className="text-3xl font-bold tabular-nums transition-all duration-300 group-hover:text-primary">{stat.value.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              </div>
              {/* Trend indicator */}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <TrendingUp className="h-4 w-4 text-success" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs with Modern Styling */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="glass p-1 gap-1">
          <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-white transition-all">
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-white transition-all">
            <Users className="h-4 w-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="tools" className="data-[state=active]:bg-primary data-[state=active]:text-white transition-all">
            <Wrench className="h-4 w-4 mr-2" />
            Tools
          </TabsTrigger>
          <TabsTrigger value="announcements" className="data-[state=active]:bg-primary data-[state=active]:text-white transition-all">
            <Bell className="h-4 w-4 mr-2" />
            Announcements
          </TabsTrigger>
          <TabsTrigger value="hosted-sites" className="data-[state=active]:bg-primary data-[state=active]:text-white transition-all">
            <Globe2 className="h-4 w-4 mr-2" />
            Hosted Sites
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Recent Users */}
            <Card className="glass border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Recent Registrations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-2 rounded-lg bg-accent/30 hover:bg-accent/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatar_url || undefined} />
                          <AvatarFallback className="bg-primary/20 text-primary text-xs">
                            {getInitials(user.username)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{user.username || 'Anonymous'}</p>
                          <p className="text-xs text-muted-foreground">
                            {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {user.is_admin && <Badge className="bg-primary text-primary-foreground">Admin</Badge>}
                        {user.is_premium && <Badge className="bg-warning text-white">Premium</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Popular Tools */}
            <Card className="glass border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Popular Tools
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {toolStats.length > 0 ? toolStats.map((tool, i) => (
                    <div key={tool.tool_name} className="flex items-center justify-between p-2 rounded-lg bg-accent/30 hover:bg-accent/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">#{i + 1}</span>
                        <span className="font-medium capitalize">{tool.tool_name.replace(/_/g, ' ')}</span>
                      </div>
                      <Badge variant="secondary">{tool.count} uses</Badge>
                    </div>
                  )) : (
                    <p className="text-muted-foreground text-center py-4">No tool usage data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <Card className="glass border-border/50">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>View and manage all registered users</CardDescription>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-full sm:w-64 bg-background/50"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => (
                        <TableRow key={user.id} className={cn(
                          "transition-colors",
                          user.is_premium
                            ? "bg-yellow-500/5 hover:bg-yellow-500/10 border-l-2 border-l-yellow-400"
                            : "hover:bg-accent/30"
                        )}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={user.avatar_url || undefined} />
                                <AvatarFallback className="bg-primary/20 text-primary">
                                  {getInitials(user.username)}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{user.username || 'Anonymous'}</p>
                                <p className="text-xs text-muted-foreground">{user.id.slice(0, 8)}...</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 flex-wrap">
                              {user.is_admin && (
                                <Badge className="bg-primary text-primary-foreground">Admin</Badge>
                              )}
                              {user.is_premium && (
                                <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white border-0 shadow-sm shadow-yellow-500/30">
                                  <Crown className="h-3 w-3 mr-1" />
                                  {user.premium_tier?.toUpperCase() || 'PREMIUM'}
                                </Badge>
                              )}
                              {!user.is_admin && !user.is_premium && (
                                <Badge variant="secondary">User</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setIsUserDialogOpen(true);
                                }}
                                className="h-8 w-8 hover:bg-primary/20"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => togglePremium(user.id, user.is_premium)}
                                className="h-8 w-8 hover:bg-warning/20"
                                title={user.is_premium ? 'Remove Premium' : 'Grant Premium'}
                              >
                                <Crown className={`h-4 w-4 ${user.is_premium ? 'text-warning' : ''}`} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => toggleAdmin(user.id, user.is_admin)}
                                className="h-8 w-8 hover:bg-primary/20"
                                title={user.is_admin ? 'Revoke Admin' : 'Make Admin'}
                              >
                                <UserCog className={`h-4 w-4 ${user.is_admin ? 'text-primary' : ''}`} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              {/* Pagination Controls */}
              {totalUsers > USERS_PER_PAGE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/50">
                  <p className="text-sm text-muted-foreground">
                    Showing {currentPage * USERS_PER_PAGE + 1}–{Math.min((currentPage + 1) * USERS_PER_PAGE, totalUsers)} of {totalUsers} users
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 0}
                      onClick={() => { setCurrentPage(p => p - 1); fetchAllUsers(currentPage - 1); }}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={(currentPage + 1) * USERS_PER_PAGE >= totalUsers}
                      onClick={() => { setCurrentPage(p => p + 1); fetchAllUsers(currentPage + 1); }}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tools">
          <Card className="glass border-border/50">
            <CardHeader>
              <CardTitle>Tools Statistics</CardTitle>
              <CardDescription>View tool usage statistics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rank</TableHead>
                      <TableHead>Tool Name</TableHead>
                      <TableHead className="text-right">Total Uses</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {toolStats.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                          No tool usage data yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      toolStats.map((tool, index) => (
                        <TableRow key={tool.tool_name} className="hover:bg-accent/30 transition-colors">
                          <TableCell>
                            <Badge variant={index < 3 ? 'default' : 'secondary'}>
                              #{index + 1}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium capitalize">
                            {tool.tool_name.replace(/_/g, ' ')}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {tool.count}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="announcements">
          <Card className="glass border-border/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Announcements</CardTitle>
                <CardDescription>Manage system announcements</CardDescription>
              </div>
              <Button onClick={() => navigate('/dashboard/admin/announcements')}>
                <Bell className="h-4 w-4 mr-2" />
                Manage
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Go to announcements page to manage notifications.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hosted-sites">
          <Card className="glass border-border/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Globe2 className="h-5 w-5" />
                  Hosted Sites
                </CardTitle>
                <CardDescription>View and manage all user-published websites</CardDescription>
              </div>
              <Button onClick={() => navigate('/dashboard/admin/hosted-sites')}>
                <Globe2 className="h-4 w-4 mr-2" />
                Manage All Sites
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Go to the hosted sites management page to view all published sites, check connectivity, and manage them.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Details Dialog */}
      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent className="glass">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>View and manage user information</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={selectedUser.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xl">
                    {getInitials(selectedUser.username)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-bold">{selectedUser.username || 'Anonymous'}</h3>
                  <p className="text-sm text-muted-foreground">ID: {selectedUser.id}</p>
                  <p className="text-sm text-muted-foreground">
                    Joined: {selectedUser.created_at ? format(new Date(selectedUser.created_at), 'MMMM d, yyyy') : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {selectedUser.is_admin && <Badge className="bg-primary">Admin</Badge>}
                {selectedUser.is_premium && (
                  <Badge className="bg-warning text-white">
                    {selectedUser.premium_tier || 'Premium'}
                  </Badge>
                )}
                {!selectedUser.is_admin && !selectedUser.is_premium && (
                  <Badge variant="secondary">Regular User</Badge>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => togglePremium(selectedUser.id, selectedUser.is_premium)}
                  className="flex-1"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  {selectedUser.is_premium ? 'Remove Premium' : 'Grant Premium'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => toggleAdmin(selectedUser.id, selectedUser.is_admin)}
                  className="flex-1"
                >
                  <UserCog className="h-4 w-4 mr-2" />
                  {selectedUser.is_admin ? 'Revoke Admin' : 'Make Admin'}
                </Button>
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete User
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete User?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this user and all their data. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteUser(selectedUser.id)}
                      className="bg-destructive hover:bg-destructive/90"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
